import { useEffect, useState } from "react";
import api from "@/api";
import { useAuth } from "@/auth";

const STATUS_META = {
  implemented:  { label: "Implementado", color: "var(--lala-green, #009639)", icon: "fa-circle-check" },
  configurable: { label: "Configurable",  color: "var(--lala-blue, #0033AD)", icon: "fa-sliders" },
  roadmap:      { label: "Roadmap Fase 2", color: "#D4A437",                  icon: "fa-route" },
};

const TAB_ICON = {
  ciberseguridad: "fa-shield-halved",
  conectividad: "fa-network-wired",
  arquitectura: "fa-cubes",
  devops: "fa-gears",
};

export default function ReadinessTecnico() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [summary, setSummary] = useState([]);
  const [activeTab, setActiveTab] = useState("ciberseguridad");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [{ data: full }, { data: sum }] = await Promise.all([
          api.get("/readiness").catch(() => ({ data: null })),
          api.get("/readiness/summary"),
        ]);
        if (full?.catalog) setCatalog(full.catalog);
        if (sum?.sections) setSummary(sum.sections);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="text-center py-20 font-hand text-2xl text-[var(--lala-blue)]">Cargando readiness…</div>;

  const isTechView = !!catalog && ["admin", "tech_admin", "evaluator", "stakeholder"].includes(user?.role);
  const canDownloadPdf = ["admin", "tech_admin", "evaluator", "stakeholder"].includes(user?.role);
  const section = catalog?.[activeTab];

  const globalReady = summary.length > 0
    ? Math.round(summary.reduce((acc, s) => acc + (s.implemented + s.configurable), 0)
        / Math.max(1, summary.reduce((acc, s) => acc + s.total, 0)) * 100)
    : null;

  const downloadPdf = async () => {
    const res = await api.get("/readiness/pdf", { responseType: "blob" });
    const blob = new Blob([res.data], { type: res.headers["content-type"] });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "lala-elite-readiness.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="px-6 py-10 max-w-7xl mx-auto" data-testid="readiness-page">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-hand text-[var(--lala-blue)] text-2xl mb-1">readiness técnico</p>
          <h1 className="text-4xl font-extrabold text-[var(--ink)]">Preparados para el assessment de TI de LALA</h1>
          <p className="text-[var(--ink-soft)] mt-2 max-w-3xl">
            Mapeo punto-por-punto de la plataforma Lala Elite contra los criterios técnicos de Grupo LALA
            (Ciberseguridad, Conectividad, Arquitectura empresarial y DevOps).
            Cada control se reporta como <strong>Implementado</strong>, <strong>Configurable</strong> o <strong>Roadmap Fase 2</strong>.
          </p>
        </div>
        {canDownloadPdf && (
          <button
            data-testid="readiness-download-pdf"
            onClick={downloadPdf}
            className="px-5 py-2.5 rounded-full bg-[var(--lala-blue)] text-white font-semibold hover:bg-[var(--lala-blue-deep)] transition flex items-center gap-2"
          >
            <i className="fa-solid fa-file-pdf"></i> Descargar PDF
          </button>
        )}
      </div>

      {globalReady !== null && (
        <div data-testid="readiness-global-banner"
          className="mb-6 p-5 rounded-2xl bg-[var(--lala-blue-deep)] text-white flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase font-bold opacity-80 tracking-wider">Readiness técnico preliminar</p>
            <p className="text-5xl font-extrabold mt-1">~{globalReady}%</p>
            <p className="text-xs opacity-75 mt-1 max-w-xl">
              Promedio ponderado de los 4 dominios. Esta cifra es <b>preliminar</b> y no constituye declaración de cumplimiento formal ni certificación.
            </p>
          </div>
          <div className="text-right text-xs opacity-80 hidden md:block">
            <p>Base: Implementado + Configurable</p>
            <p className="mt-1">sobre el total de criterios catalogados</p>
          </div>
        </div>
      )}

      {/* Fase 1 — Postura del proveedor */}
      <div className="mb-6 rounded-2xl border-2 border-dashed border-[var(--lala-blue)]/40 bg-[var(--lala-blue-soft)] p-5" data-testid="phase1-posture">
        <div className="text-xs uppercase tracking-wider font-bold text-[var(--lala-blue)] mb-1">Postura Fase 1 · superficie de escritura</div>
        <p className="text-sm text-[var(--ink)] leading-relaxed max-w-4xl">
          En Fase 1, el <b>proveedor opera en modo solo-lectura</b>. La carga de scores proviene exclusivamente de Lala
          (entrada manual o CSV/Excel). Auto-evaluación y evidencia documental
          quedan como <b>Fase 2</b> detrás de feature flags. Esto reduce la superficie de escritura externa y simplifica el modelo de riesgo operativo y de seguridad.
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {summary.map((s) => (
          <button
            key={s.key}
            data-testid={`readiness-card-${s.key}`}
            onClick={() => setActiveTab(s.key)}
            className={`text-left p-5 rounded-2xl border-2 transition ${activeTab === s.key
              ? "bg-white border-[var(--lala-blue)] shadow-md"
              : "bg-white/60 border-transparent hover:border-[var(--lala-blue)]/30"}`}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white bg-[var(--lala-blue)]">
                <i className={`fa-solid ${TAB_ICON[s.key]}`}></i>
              </div>
              <h3 className="font-bold text-[var(--ink)]">{s.title}</h3>
            </div>
            <p className="text-xs text-[var(--ink-soft)] mb-3 leading-snug">{s.subtitle}</p>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-3xl font-extrabold text-[var(--lala-blue)]">{s.pct_ready}%</span>
              <span className="text-xs text-[var(--ink-soft)] font-bold uppercase">listo</span>
            </div>
            <div className="w-full h-2 bg-[var(--cream)] rounded-full overflow-hidden">
              <div className="h-full bg-[var(--lala-green)]" style={{ width: `${s.pct_implemented}%` }}></div>
              <div className="h-full bg-[var(--lala-blue)]" style={{ width: `${100 * s.configurable / s.total}%`, marginTop: -8 }}></div>
            </div>
            <p className="text-xs text-[var(--ink-soft)] mt-2">
              <span className="text-[var(--lala-green)] font-bold">{s.implemented}</span> impl. ·{" "}
              <span className="text-[var(--lala-blue)] font-bold">{s.configurable}</span> conf. ·{" "}
              <span className="text-[#D4A437] font-bold">{s.roadmap}</span> roadmap
            </p>
          </button>
        ))}
      </div>

      {/* Tabs */}
      {isTechView && (
        <>
          <div className="flex gap-2 mb-6 border-b border-[var(--lala-blue)]/10 overflow-x-auto">
            {Object.entries(catalog).map(([key, sec]) => (
              <button
                key={key}
                data-testid={`readiness-tab-${key}`}
                onClick={() => setActiveTab(key)}
                className={`px-5 py-3 font-semibold whitespace-nowrap border-b-4 transition ${activeTab === key
                  ? "border-[var(--lala-blue)] text-[var(--lala-blue)]"
                  : "border-transparent text-[var(--ink-soft)] hover:text-[var(--ink)]"}`}
              >
                <i className={`fa-solid ${TAB_ICON[key]} mr-2`}></i>
                {sec.title}
              </button>
            ))}
          </div>

          {section && (
            <div className="space-y-6">
              <p className="text-[var(--ink-soft)] italic">{section.subtitle}</p>
              {section.groups.map((g) => (
                <div key={g.name} className="bg-white/70 rounded-2xl p-6 border border-[var(--lala-blue)]/10">
                  <h3 className="font-bold text-lg text-[var(--ink)] mb-4">{g.name}</h3>
                  <div className="space-y-3">
                    {g.items.map((it) => {
                      const sm = STATUS_META[it.status];
                      return (
                        <div key={it.name} className="flex gap-4 p-3 rounded-xl bg-[var(--cream)]/50">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm" style={{ background: sm.color }}>
                            <i className={`fa-solid ${sm.icon}`}></i>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                              <p className="font-semibold text-[var(--ink)]">{it.name}</p>
                              <span className="text-xs uppercase font-bold px-2 py-1 rounded-full" style={{ background: sm.color, color: "white" }}>
                                {sm.label}
                              </span>
                            </div>
                            <p className="text-sm text-[var(--ink-soft)] mt-1">{it.notes}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Architecture diagram (simple SVG) */}
      <section className="mt-12 bg-white/70 rounded-2xl p-8 border border-[var(--lala-blue)]/10">
        <h2 className="text-2xl font-bold text-[var(--ink)] mb-2">Diagrama lógico de arquitectura</h2>
        <p className="text-[var(--ink-soft)] mb-6">Componentes principales y dependencias del sistema.</p>
        <ArchitectureDiagram />
      </section>
    </div>
  );
}

function ArchitectureDiagram() {
  return (
    <svg viewBox="0 0 900 460" className="w-full max-w-4xl mx-auto">
      <defs>
        <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#0033AD"/>
        </marker>
      </defs>
      {/* Layer: Frontend */}
      <g>
        <rect x="30" y="30" width="840" height="80" rx="14" fill="#FFFFFF" stroke="#0033AD" strokeWidth="2"/>
        <text x="50" y="55" fontFamily="Helvetica" fontWeight="700" fontSize="13" fill="#0033AD">FRONTEND · React 18 + Tailwind + shadcn/ui</text>
        <g transform="translate(50,70)">
          {["Landing pública", "Dashboard LALA", "Vista Proveedor", "Vista Stakeholder", "Vista Comprador", "Readiness", "Configuración"].map((l, i) => (
            <g key={l} transform={`translate(${i * 115},0)`}>
              <rect width="105" height="26" rx="6" fill="#F0F4FB" stroke="#0033AD33"/>
              <text x="52" y="17" textAnchor="middle" fontSize="10" fontFamily="Helvetica" fill="#11142B">{l}</text>
            </g>
          ))}
        </g>
      </g>
      {/* Arrows */}
      <line x1="450" y1="115" x2="450" y2="155" stroke="#0033AD" strokeWidth="2" markerEnd="url(#arrow)"/>
      {/* Layer: API */}
      <g>
        <rect x="30" y="160" width="840" height="80" rx="14" fill="#FFFFFF" stroke="#0033AD" strokeWidth="2"/>
        <text x="50" y="185" fontFamily="Helvetica" fontWeight="700" fontSize="13" fill="#0033AD">BACKEND API · FastAPI + Python 3.11 (async)</text>
        <g transform="translate(50,200)">
          {["Auth + JWT", "Scoring engine", "Audit log", "Notif center", "PDF gen", "Mocks integr."].map((l, i) => (
            <g key={l} transform={`translate(${i * 115},0)`}>
              <rect width="105" height="26" rx="6" fill="#E4002B11" stroke="#E4002B55"/>
              <text x="52" y="17" textAnchor="middle" fontSize="10" fontFamily="Helvetica" fill="#11142B">{l}</text>
            </g>
          ))}
        </g>
      </g>
      <line x1="220" y1="245" x2="220" y2="285" stroke="#0033AD" strokeWidth="2" markerEnd="url(#arrow)"/>
      <line x1="680" y1="245" x2="680" y2="285" stroke="#0033AD" strokeWidth="2" strokeDasharray="4 4" markerEnd="url(#arrow)"/>
      {/* Layer: Data / Integrations */}
      <g>
        <rect x="30" y="290" width="380" height="140" rx="14" fill="#FFFFFF" stroke="#009639" strokeWidth="2"/>
        <text x="50" y="315" fontFamily="Helvetica" fontWeight="700" fontSize="13" fill="#009639">DATOS</text>
        <text x="50" y="340" fontSize="11" fontFamily="Helvetica" fill="#11142B">MongoDB · colecciones:</text>
        <text x="50" y="358" fontSize="10" fontFamily="Helvetica" fill="#11142B">users · suppliers · evaluations · scores</text>
        <text x="50" y="372" fontSize="10" fontFamily="Helvetica" fill="#11142B">audit_logs · notifications · documents</text>
        <text x="50" y="386" fontSize="10" fontFamily="Helvetica" fill="#11142B">learning_resources · questionnaires</text>
        <text x="50" y="400" fontSize="10" fontFamily="Helvetica" fill="#11142B">program_config · integration_config</text>
        <text x="50" y="418" fontSize="10" fontStyle="italic" fontFamily="Helvetica" fill="#11142B">Cifrado en reposo configurable.</text>
      </g>
      <g>
        <rect x="490" y="290" width="380" height="140" rx="14" fill="#FFFFFF" stroke="#D4A437" strokeWidth="2"/>
        <text x="510" y="315" fontFamily="Helvetica" fontWeight="700" fontSize="13" fill="#B8860B">INTEGRACIONES EXTERNAS</text>
        <g transform="translate(510,332)">
          {[
            { label: "SAP (datos maestros, ID proveedor)", status: "mock" },
            { label: "Azure AD / Entra ID (SSO)", status: "roadmap" },
          ].map((it, i) => (
            <g key={it.label} transform={`translate(0,${i * 19})`}>
              <circle cx="6" cy="8" r="4" fill={it.status === "live" ? "#009639" : it.status === "mock" ? "#0033AD" : "#D4A437"}/>
              <text x="18" y="12" fontSize="10" fontFamily="Helvetica" fill="#11142B">{it.label}</text>
              <text x="305" y="12" fontSize="9" fontFamily="Helvetica" fontStyle="italic" fill="#666">
                {it.status === "live" ? "(activo)" : it.status === "mock" ? "(mock listo)" : "(roadmap)"}
              </text>
            </g>
          ))}
        </g>
      </g>
    </svg>
  );
}
